import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Link, useParams } from '@tanstack/react-router';
import {
  Banner,
  Button,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  LoadingOrbit,
  Markdown,
  Page,
  PageHeader,
  TagBadge,
  Text,
} from '@chatsdet/ui';
import { Robot } from '@phosphor-icons/react';
import { formatPassPercent } from '@chatsdet/types/agent-eval-result';
import type { AgentEvalTemplate } from '@chatsdet/types/agent-eval-template';
import { ApiError } from '../../../lib/api-error';
import { formatRelativeTimeAgo } from '../../../features/workflows/useAgenticRunDetail';
import {
  formatCostCents,
  runVerdictPresentation,
  shortSha,
} from '../../../features/agent-evals/agentEvalPresentation';
import {
  useAgentEval,
  useAgentEvalRunbook,
  useAgentEvalRuns,
  type AgentEvalRunListItem,
} from '../../../features/agent-evals/useAgentEvals';

const RUN_COLUMNS: ColumnDef<StockFeatures, AgentEvalRunListItem>[] = [
  {
    id: 'verdict',
    header: 'Verdict',
    enableSorting: false,
    cell: ({ row }) => {
      const { label, color } = runVerdictPresentation(row.original);
      return <TagBadge name={label} color={color} size="md" />;
    },
    meta: { minWidth: 120 },
  },
  {
    id: 'score',
    header: 'Score',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.headline ? (
        <Text size="sm" className="tabular-nums">
          {`${formatPassPercent(row.original.headline.passRate)} / ${formatPassPercent(row.original.headline.passBar)} bar`}
        </Text>
      ) : (
        <Text size="sm" className="text-muted-foreground">—</Text>
      ),
    meta: { minWidth: 150 },
  },
  {
    id: 'model',
    header: 'Model',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm">
        {row.original.modelAlias}
        <span className="text-muted-foreground">{` (${row.original.model})`}</span>
      </Text>
    ),
    meta: { flex: 2, minWidth: 180 },
  },
  {
    id: 'cost',
    header: 'Cost',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.headline ? (
        <Text size="sm" className="tabular-nums">{formatCostCents(row.original.headline.costCents)}</Text>
      ) : (
        <Text size="sm" className="text-muted-foreground">—</Text>
      ),
    meta: { minWidth: 100 },
  },
  {
    id: 'commit',
    header: 'Commit',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="font-mono">{shortSha(row.original.commitSha)}</Text>,
    meta: { minWidth: 100 },
  },
  {
    id: 'when',
    header: 'When',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">{formatRelativeTimeAgo(row.original.createdAt)}</Text>
    ),
    meta: { minWidth: 110 },
  },
];

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" className="text-muted-foreground">{label}</Text>
      <Text size="sm" className="tabular-nums">{value}</Text>
    </div>
  );
}

function TemplateCard({ definition }: { definition: AgentEvalTemplate }) {
  return (
    <Card>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Fact label="Mode" value={definition.mode} />
          <Fact label="Agent default model" value={definition.agent.default} />
          <Fact label="Pass bar" value={formatPassPercent(definition.passBar)} />
          <Fact label="Repeats" value={`${definition.repeats} (pass at least ${definition.passAtLeast})`} />
        </div>
      </CardContent>
    </Card>
  );
}

export function AgentEvalPage() {
  const { name } = useParams({ from: '/agent-evals/$name' });
  const evaluation = useAgentEval(name);
  const detail = evaluation.data;
  const runbook = useAgentEvalRunbook(name, Boolean(detail));
  const runs = useAgentEvalRuns(name, Boolean(detail));
  const items = runs.data?.pages.flatMap((p) => p.items) ?? [];

  if (evaluation.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }
  if (!detail && evaluation.isError && !(evaluation.error instanceof ApiError && evaluation.error.status === 404)) {
    return (
      <Page>
        <Banner variant="error">{evaluation.error.message}</Banner>
      </Page>
    );
  }
  if (!detail) {
    return (
      <Page>
        <EmptyState icon={<Robot size={48} />} title="Eval not found" description="This eval does not exist." />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader icon={<Robot size={24} weight="fill" />} title={detail.name} description={detail.description} />
      <Link to="/agent-evals" className="text-sm font-medium text-foreground hover:underline">
        All evals
      </Link>
      <TemplateCard definition={detail.definition} />

      <PageHeader size="section" title={`Cases (${detail.cases.length})`} />
      <Card>
        <CardContent>
          <ul className="space-y-2">
            {detail.cases.map((c) => (
              <li key={c.key}>
                <Text size="sm" className="font-medium">{c.title}</Text>
                <Text size="xs" className="font-mono text-muted-foreground">{c.key}</Text>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {detail.capabilities.length > 0 && (
        <>
          <PageHeader size="section" title="Proves capabilities" />
          <Card>
            <CardContent>
              <ul className="space-y-1">
                {detail.capabilities.map((cap) => (
                  <li key={cap.id}>
                    <Text size="sm">
                      <span className="font-mono">{cap.code}</span>
                      {` ${cap.title}`}
                    </Text>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      )}

      <PageHeader size="section" title="Run history" />
      {runs.isError && items.length === 0 ? (
        <Banner variant="error">{runs.error.message}</Banner>
      ) : runs.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={<Robot size={48} />} title="No runs yet" description="Run this eval with the CLI to see results here." />
      ) : (
        <DataTable
          columns={RUN_COLUMNS}
          data={items}
          aria-label="Eval runs table"
          getRowId={(row) => row.id}
          getRowHref={(row) => `/agent-evals/runs/${row.id}`}
        />
      )}
      {runs.isError && items.length > 0 && <Banner variant="error">{runs.error.message}</Banner>}
      {runs.hasNextPage && (
        <div className="flex justify-center py-4">
          <Button variant="outline" onPress={() => runs.fetchNextPage()} isDisabled={runs.isFetchingNextPage}>
            {runs.isFetchingNextPage ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}

      {runbook.isError && <Banner variant="error">{runbook.error.message}</Banner>}
      {runbook.data && (
        <>
          <PageHeader size="section" title="Runbook" />
          <Card>
            <CardContent>
              <Markdown>{runbook.data}</Markdown>
            </CardContent>
          </Card>
        </>
      )}
    </Page>
  );
}
